import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Button, Tag, Space, Typography, Alert, Table, Modal, App as AntApp,
  Select, Input, Switch, InputNumber, Tooltip,
} from 'antd';
import { ReloadOutlined, DownloadOutlined, SyncOutlined, MailOutlined, PaperClipOutlined, CheckCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { API_URL } from '../config';

const { Title, Text, Paragraph } = Typography;

async function getToken(): Promise<string> {
  const s = await fetchAuthSession();
  return s.tokens?.idToken?.toString() ?? '';
}

interface EmailRow {
  id: number;
  assunto: string | null;
  remetente_email: string | null;
  remetente_nome: string | null;
  data_recebido: string;
  corpo_preview: string | null;
  tem_anexos: boolean;
  qtd_anexos: number;
  contrato_id: string | null;
  contrato_numero: string | null;
  contrato_titulo: string | null;
  // Contratos distintos dos ITENS (anexos + links) deste e-mail — "HY26012, HY26015"
  contratos_itens: string | null;
  processado: boolean;
  // Agregados de download por ITEM (anexos + links): quantos já foram pegos
  itens_total?: number;
  itens_baixados?: number;
  ultimo_download_em?: string | null;
  ultimo_baixado_por?: string | null;
}

interface AnexoMeta {
  id: number;
  nome: string;
  mime_type: string | null;
  tamanho_bytes: number | null;
  s3_key: string;
  contrato_id: string | null;
  contrato_numero: string | null;
  // Histórico completo de downloads (mais recente primeiro)
  downloads?: Array<{ usuario_email: string; baixado_em: string }>;
}

interface LinkMeta {
  id: number;
  url: string;
  dominio: string | null;
  texto_ancora: string | null;
  contrato_id: string | null;
  contrato_numero: string | null;
  downloads?: Array<{ usuario_email: string; baixado_em: string }>;
}

interface EmailFull extends EmailRow {
  internet_message_id: string | null;
  conversation_id: string | null;
  destinatarios: Array<{ email: string; nome: string }> | null;
  cc: Array<{ email: string; nome: string }> | null;
  data_enviado: string | null;
  corpo_html: string | null;
  corpo_texto: string | null;
  observacao: string | null;
}

interface Config {
  caixa_email: string;
  ultima_sync_em: string | null;
  ultima_sync_qtd: number | null;
  ultima_sync_erro: string | null;
}

async function chamarApi<T = any>(action: string, payload: Record<string, any> = {}): Promise<T> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/email-dados`, { action, ...payload }, {
    headers: { Authorization: token, 'Content-Type': 'application/json' },
  });
  const raw: any = r.data;
  return (raw?.body ? JSON.parse(raw.body) : raw) as T;
}

function fmtTamanho(b: number | null): string {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function DadosCampoInner() {
  const { message } = AntApp.useApp();
  const [emails, setEmails] = useState<EmailRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [config, setConfig] = useState<Config | null>(null);
  const [secretOk, setSecretOk] = useState(false);

  const [filtroRemetente, setFiltroRemetente] = useState('');
  const [filtroAssunto, setFiltroAssunto] = useState('');
  const [filtroComAnexo, setFiltroComAnexo] = useState(false);
  const [filtroContrato, setFiltroContrato] = useState<string | null>(null);
  const [contratos, setContratos] = useState<Array<{ id: string; numero: string; titulo: string }>>([]);

  const [dias, setDias] = useState(10);

  const [modalAberto, setModalAberto] = useState(false);
  const [emailSelecionado, setEmailSelecionado] = useState<EmailFull | null>(null);
  const [anexos, setAnexos] = useState<AnexoMeta[]>([]);
  const [links, setLinks] = useState<LinkMeta[]>([]);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);

  const recarregarDetalhe = useCallback(async (id: number) => {
    const r = await chamarApi<{ email: EmailFull; anexos: AnexoMeta[]; links: LinkMeta[] }>('get', { id });
    if (r.anexos) setAnexos(r.anexos);
    if (r.links) setLinks(r.links);
  }, []);

  const carregarConfig = useCallback(async () => {
    try {
      const r = await chamarApi<{ config: Config | null; secret_ok: boolean }>('config_get');
      setConfig(r.config);
      setSecretOk(r.secret_ok);
    } catch (e: any) {
      console.warn('config_get:', e.message);
    }
  }, []);

  const carregarLista = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const payload: Record<string, any> = { limit: 100, offset: 0 };
      if (filtroRemetente) payload.remetente = filtroRemetente;
      if (filtroAssunto) payload.assunto = filtroAssunto;
      if (filtroComAnexo) payload.tem_anexos = true;
      if (filtroContrato) payload.contrato_id = filtroContrato;
      const r = await chamarApi<{ emails: EmailRow[]; total: number }>('list', payload);
      setEmails(r.emails || []);
      setTotal(r.total || 0);
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [filtroRemetente, filtroAssunto, filtroComAnexo, filtroContrato]);

  useEffect(() => {
    carregarConfig();
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/mobile/contratos`, { headers: { Authorization: token } });
        const raw: any = r.data;
        const data: any = raw?.body ? JSON.parse(raw.body) : raw;
        setContratos(data?.contratos || []);
      } catch { /* silencioso */ }
    })();
  }, [carregarConfig]);

  useEffect(() => { carregarLista(); }, [carregarLista]);

  async function sincronizar() {
    setSincronizando(true);
    setErro(null);
    try {
      const r = await chamarApi<{ novos: number; atualizados: number; anexos_baixados: number; falhas: string[]; total_listado: number }>('sync', { dias });
      message.success(`Sync OK: ${r.novos} novos, ${r.atualizados} já tinha, ${r.anexos_baixados} anexos baixados (de ${r.total_listado} no Outlook)`);
      if (r.falhas?.length) {
        message.warning(`${r.falhas.length} falha(s) — ver detalhe abaixo`);
        setErro(r.falhas.join('\n'));
      }
      await carregarConfig();
      await carregarLista();
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
    } finally {
      setSincronizando(false);
    }
  }

  async function abrirDetalhe(e: EmailRow) {
    setEmailSelecionado(null);
    setAnexos([]);
    setLinks([]);
    setModalAberto(true);
    setCarregandoDetalhe(true);
    try {
      const r = await chamarApi<{ email: EmailFull; anexos: AnexoMeta[]; links: LinkMeta[] }>('get', { id: e.id });
      setEmailSelecionado(r.email);
      setAnexos(r.anexos || []);
      setLinks(r.links || []);
    } catch (er: any) {
      message.error('Falha ao abrir: ' + (er.response?.data?.error || er.message));
    } finally {
      setCarregandoDetalhe(false);
    }
  }

  async function baixarAnexo(a: AnexoMeta) {
    try {
      const r = await chamarApi<{ url: string }>('anexo_url', { anexo_id: a.id });
      window.open(r.url, '_blank');
      if (emailSelecionado) recarregarDetalhe(emailSelecionado.id).catch(() => {});
    } catch (e: any) {
      message.error('Falha ao baixar: ' + (e.response?.data?.error || e.message));
    }
  }

  // Classificação é por ITEM (anexo/link) — o e-mail não tem mais contrato.
  async function vincularAnexo(anexoId: number, contratoId: string | null) {
    try {
      await chamarApi('anexo_vincular_contrato', { anexo_id: anexoId, contrato_id: contratoId });
      if (emailSelecionado) await recarregarDetalhe(emailSelecionado.id);
      await carregarLista();
    } catch (e: any) {
      message.error('Falha: ' + e.message);
    }
  }

  // Contrato de UM link
  async function vincularLink(linkId: number, contratoId: string | null) {
    try {
      await chamarApi('link_vincular_contrato', { link_id: linkId, contrato_id: contratoId });
      if (emailSelecionado) await recarregarDetalhe(emailSelecionado.id);
      await carregarLista();
    } catch (e: any) {
      message.error('Falha: ' + e.message);
    }
  }

  // Marca um link como baixado (registra no histórico)
  async function marcarLinkBaixado(linkId: number) {
    try {
      await chamarApi('link_marcar_baixado', { link_id: linkId });
      message.success('Marcado como baixado');
      if (emailSelecionado) await recarregarDetalhe(emailSelecionado.id);
    } catch (e: any) {
      message.error('Falha: ' + e.message);
    }
  }

  const colunas = [
    {
      title: 'Recebido',
      dataIndex: 'data_recebido',
      width: 150,
      render: (d: string) => <Text style={{ fontSize: 12 }}>{new Date(d).toLocaleString('pt-BR')}</Text>,
    },
    {
      title: 'Remetente',
      dataIndex: 'remetente_email',
      width: 220,
      render: (_: any, row: EmailRow) => (
        <div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>{row.remetente_nome || row.remetente_email || '—'}</div>
          {row.remetente_nome && <div style={{ fontSize: 11, color: '#888' }}>{row.remetente_email}</div>}
        </div>
      ),
    },
    {
      title: 'Assunto',
      dataIndex: 'assunto',
      render: (_: any, row: EmailRow) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: 13 }}>{row.assunto || '(sem assunto)'}</div>
          {row.corpo_preview && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{row.corpo_preview.slice(0, 120)}{row.corpo_preview.length > 120 ? '…' : ''}</div>}
        </div>
      ),
    },
    {
      title: 'Itens',
      dataIndex: 'itens_total',
      width: 100,
      align: 'center' as const,
      render: (_: any, row: EmailRow) => {
        const tot = row.itens_total ?? row.qtd_anexos ?? 0;
        if (!tot) return <span style={{ color: '#ccc' }}>—</span>;
        const baix = row.itens_baixados || 0;
        // Verde quando todos os itens (anexos + links) têm ≥1 download.
        const cor = baix >= tot ? 'success' : baix > 0 ? 'gold' : 'blue';
        return <Tag icon={<PaperClipOutlined />} color={cor}>{baix}/{tot}</Tag>;
      },
    },
    {
      title: 'Baixado',
      dataIndex: 'ultimo_baixado_por',
      width: 160,
      render: (_: any, row: EmailRow) => {
        if (!row.ultimo_baixado_por) return <span style={{ color: '#ccc', fontSize: 11 }}>—</span>;
        const nome = (row.ultimo_baixado_por || '').split('@')[0];
        const dt = row.ultimo_download_em ? new Date(row.ultimo_download_em) : null;
        const fmt = dt ? `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')} ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}` : '';
        return (
          <Tooltip title={`Último por ${row.ultimo_baixado_por} em ${fmt}`}>
            <span style={{ fontSize: 11, color: '#0a7f4a' }}>📥 <strong>{nome}</strong> {fmt}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Contrato(s)',
      dataIndex: 'contratos_itens',
      width: 150,
      render: (_: any, row: EmailRow) => {
        if (!row.contratos_itens) return <span style={{ color: '#ccc', fontSize: 12 }}>não classificado</span>;
        return <Space size={4} wrap>{row.contratos_itens.split(', ').map(n => <Tag key={n}>{n}</Tag>)}</Space>;
      },
    },
  ];

  const semSecret = !secretOk;

  return (
    <div style={{ padding: 20 }}>
      <Title level={3} style={{ marginBottom: 4 }}>
        <MailOutlined /> Dados de Campo — caixa <code>dados@hydrodataeng.com.br</code>
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Emails enviados pela equipe de campo (coletoras, GNSS, etc.) chegam aqui. Os anexos são baixados pro S3 e ficam disponíveis pra download. Em breve: amarrar automático ao contrato e processar com IA.
      </Paragraph>

      {semSecret && (
        <Alert
          type="warning"
          showIcon
          message="Credenciais Microsoft Graph ainda não configuradas"
          description="Salve o segredo dre/outlook-mail-reader no AWS Secrets Manager (tenant_id, client_id, client_secret, caixa_email)."
          style={{ marginBottom: 16 }}
        />
      )}

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Text strong>Sincronização:</Text>
          <span>últimos</span>
          <InputNumber min={1} max={60} value={dias} onChange={(v) => setDias(Number(v) || 10)} style={{ width: 70 }} />
          <span>dias</span>
          <Button
            type="primary"
            icon={<SyncOutlined spin={sincronizando} />}
            onClick={sincronizar}
            loading={sincronizando}
            disabled={semSecret}
          >
            Sincronizar agora
          </Button>
          {config?.ultima_sync_em && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Última: {new Date(config.ultima_sync_em).toLocaleString('pt-BR')}
              {config.ultima_sync_qtd != null ? ` · ${config.ultima_sync_qtd} novos` : ''}
              {config.ultima_sync_erro ? <span style={{ color: '#f5222d' }}> · com erros</span> : ''}
            </Text>
          )}
        </Space>
      </Card>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="Filtrar remetente"
            value={filtroRemetente}
            onChange={(e) => setFiltroRemetente(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Input
            placeholder="Filtrar assunto"
            value={filtroAssunto}
            onChange={(e) => setFiltroAssunto(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            placeholder="Contrato"
            value={filtroContrato}
            onChange={setFiltroContrato}
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 240 }}
            options={contratos.map((c) => ({ value: c.id, label: `${c.numero} — ${c.titulo}` }))}
          />
          <Space>
            <Switch checked={filtroComAnexo} onChange={setFiltroComAnexo} size="small" />
            <Text style={{ fontSize: 13 }}>Com anexo</Text>
          </Space>
          <Button icon={<ReloadOutlined />} onClick={carregarLista} loading={loading}>Recarregar</Button>
        </Space>
      </Card>

      {erro && <Alert type="error" message={erro} showIcon closable onClose={() => setErro(null)} style={{ marginBottom: 16, whiteSpace: 'pre-wrap' }} />}

      <Text type="secondary" style={{ fontSize: 12 }}>Total: {total}</Text>

      <Table
        dataSource={emails}
        columns={colunas}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        onRow={(row) => {
          // Verde claro quando todos os itens (anexos + links) com ≥1 download
          const tot = row.itens_total ?? row.qtd_anexos ?? 0;
          const todosBaixados = tot > 0 && (row.itens_baixados || 0) >= tot;
          return {
            onClick: () => abrirDetalhe(row),
            style: {
              cursor: 'pointer',
              backgroundColor: todosBaixados ? '#f0fdf4' : undefined,
            },
          };
        }}
        size="small"
        style={{ marginTop: 8 }}
      />

      <Modal
        open={modalAberto}
        onCancel={() => setModalAberto(false)}
        footer={null}
        width={900}
        title={emailSelecionado?.assunto || (carregandoDetalhe ? 'Carregando...' : '')}
      >
        {emailSelecionado && (
          <div>
            <div style={{ marginBottom: 12, padding: 12, background: '#fafafa', borderRadius: 4 }}>
              <div><Text strong>De: </Text>{emailSelecionado.remetente_nome} &lt;{emailSelecionado.remetente_email}&gt;</div>
              <div><Text strong>Para: </Text>{(emailSelecionado.destinatarios || []).map(d => d.email).join(', ')}</div>
              {emailSelecionado.cc?.length ? <div><Text strong>Cc: </Text>{emailSelecionado.cc.map(d => d.email).join(', ')}</div> : null}
              <div><Text strong>Recebido: </Text>{new Date(emailSelecionado.data_recebido).toLocaleString('pt-BR')}</div>
            </div>

            <Paragraph type="secondary" style={{ fontSize: 11 }}>
              Classifique cada anexo e link no contrato correspondente abaixo. Um mesmo
              e-mail pode ter itens de contratos diferentes.
            </Paragraph>

            {anexos.length > 0 && (
              <Card size="small" title={`Anexos (${anexos.length})`} style={{ marginBottom: 12 }}>
                <Space direction="vertical" style={{ width: '100%' }} size={10}>
                  {anexos.map(a => {
                    const dls = a.downloads || [];
                    return (
                      <div key={a.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <PaperClipOutlined />
                          <Text>{a.nome}</Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>{a.mime_type} · {fmtTamanho(a.tamanho_bytes)}</Text>
                          <Button size="small" icon={<DownloadOutlined />} onClick={() => baixarAnexo(a)}>Baixar</Button>
                          <Select
                            size="small" placeholder="Contrato deste anexo"
                            value={a.contrato_id || undefined}
                            onChange={(v) => vincularAnexo(a.id, v || null)}
                            allowClear showSearch optionFilterProp="label" style={{ width: 220 }}
                            options={contratos.map((c) => ({ value: c.id, label: `${c.numero} — ${c.titulo}` }))}
                          />
                        </div>
                        {dls.length > 0 && (
                          <Text type="secondary" style={{ fontSize: 11, marginLeft: 22 }}>
                            📥 Já baixaram: {dls.map((d, i) => {
                              const nome = (d.usuario_email || '').split('@')[0];
                              const dt = new Date(d.baixado_em);
                              const fmt = `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')} ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
                              return <span key={i}>{i > 0 ? ' · ' : ''}<strong>{nome}</strong> {fmt}</span>;
                            })}
                          </Text>
                        )}
                      </div>
                    );
                  })}
                </Space>
              </Card>
            )}

            {links.length > 0 && (
              <Card size="small" title={`Links de dados (${links.length})`} style={{ marginBottom: 12 }}>
                <Space direction="vertical" style={{ width: '100%' }} size={10}>
                  {links.map(l => {
                    const dls = l.downloads || [];
                    const jaBaixado = dls.length > 0;
                    return (
                      <div key={l.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <LinkOutlined style={{ color: '#0930D0' }} />
                          {l.dominio && <Tag color="blue" style={{ margin: 0 }}>{l.dominio}</Tag>}
                          <a href={l.url} target="_blank" rel="noopener noreferrer"
                            style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {l.texto_ancora || l.url}
                          </a>
                          <Button size="small" type={jaBaixado ? 'default' : 'primary'}
                            icon={<CheckCircleOutlined />} onClick={() => marcarLinkBaixado(l.id)}>
                            {jaBaixado ? 'Baixar de novo' : 'Marcar baixado'}
                          </Button>
                          <Select
                            size="small" placeholder="Contrato deste link"
                            value={l.contrato_id || undefined}
                            onChange={(v) => vincularLink(l.id, v || null)}
                            allowClear showSearch optionFilterProp="label" style={{ width: 220 }}
                            options={contratos.map((c) => ({ value: c.id, label: `${c.numero} — ${c.titulo}` }))}
                          />
                        </div>
                        {dls.length > 0 && (
                          <Text type="secondary" style={{ fontSize: 11, marginLeft: 22 }}>
                            ✅ Já baixaram: {dls.map((d, i) => {
                              const nome = (d.usuario_email || '').split('@')[0];
                              const dt = new Date(d.baixado_em);
                              const fmt = `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')} ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
                              return <span key={i}>{i > 0 ? ' · ' : ''}<strong>{nome}</strong> {fmt}</span>;
                            })}
                          </Text>
                        )}
                      </div>
                    );
                  })}
                </Space>
              </Card>
            )}

            <Card size="small" title="Conteúdo">
              {emailSelecionado.corpo_html
                ? <div style={{ maxHeight: 400, overflow: 'auto' }} dangerouslySetInnerHTML={{ __html: emailSelecionado.corpo_html }} />
                : <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{emailSelecionado.corpo_texto || emailSelecionado.corpo_preview || '(sem corpo)'}</Paragraph>}
            </Card>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default function OperacionalDadosCampo() {
  return (
    <AntApp>
      <DadosCampoInner />
    </AntApp>
  );
}
